import type { ComponentType, SVGProps } from 'react';
import PercentageGraphIcon from '../../../assets/icons/percentage-graph.svg?react';
import PeriodGraphIcon from '../../../assets/icons/period-graph.svg?react';
import SpikesGraphIcon from '../../../assets/icons/spikes-graph.svg?react';
import { NumberInput } from '../../NumberInput';
import { SignalTriggerSettings } from './context';
import { useAddAlarmDialog } from './useAddAlarmDialog';

const TRIGGER_TYPES = [
  {
    type: 'percentage' as const,
    title: 'Percentage',
    description:
      'Alert triggers when values exceed a certain percentage of the evaluation window.',
    Icon: PercentageGraphIcon,
  },
  {
    type: 'period' as const,
    title: 'Period',
    description:
      'Alert triggers when values exceed the threshold for this many consecutive minutes.',
    Icon: PeriodGraphIcon,
  },
  {
    type: 'spike' as const,
    title: 'Spikes',
    description:
      'Alert triggers when values exceed the threshold this many times during the evaluation window.',
    Icon: SpikesGraphIcon,
  },
];

interface AlarmTriggerLogicControlsProps {
  compact?: boolean;
  heading?: string;
  idPrefix?: string;
  value?: SignalTriggerSettings;
  onChange?: (updates: Partial<SignalTriggerSettings>) => void;
  advancedSettingsExpanded?: boolean;
  onToggleAdvancedSettings?: () => void;
}

export function AlarmTriggerLogicControls({
  compact = false,
  heading = 'Trigger type',
  idPrefix = 'trigger-logic',
  value,
  onChange,
  advancedSettingsExpanded,
  onToggleAdvancedSettings,
}: AlarmTriggerLogicControlsProps) {
  const {
    formData,
    updateFormData,
    advancedSettingsExpanded: globalAdvancedSettingsExpanded,
    setAdvancedSettingsExpanded,
  } = useAddAlarmDialog();

  const triggerSettings = value || {
    triggerLogicType: formData.triggerLogicType,
    percentageThreshold: formData.percentageThreshold,
    evaluationWindow: formData.evaluationWindow,
    periodMinutes: formData.periodMinutes,
    numberOfSpikes: formData.numberOfSpikes,
    cooldownPeriod: formData.cooldownPeriod,
    triggerType: formData.triggerType,
    threshold: formData.threshold,
    thresholdMax: formData.thresholdMax,
  };

  const isAdvancedExpanded = advancedSettingsExpanded ?? globalAdvancedSettingsExpanded;
  const updateTriggerSettings =
    onChange ||
    ((updates: Partial<SignalTriggerSettings>) =>
      updateFormData(updates));
  const toggleAdvancedSettings =
    onToggleAdvancedSettings ||
    (() => setAdvancedSettingsExpanded(!globalAdvancedSettingsExpanded));

  return (
    <>
      <div className={`flex flex-col ${compact ? 'gap-1' : 'gap-2'}`}>
        {heading && <label className='size-p2 text-primary'>{heading}</label>}

        <div
          className={`flex flex-col gap-1 overflow-hidden rounded-lg ${
            compact ? '' : 'p-2'
          }`}
        >
          {TRIGGER_TYPES.map(({ type, title, description, Icon }) => (
            <TriggerLogicOption
              key={type}
              title={title}
              description={description}
              Icon={Icon}
              isSelected={triggerSettings.triggerLogicType === type}
              onClick={() => updateTriggerSettings({ triggerLogicType: type })}
              compact={compact}
            />
          ))}
        </div>
      </div>

      <div className='border-border-secondary mt-auto w-full border-t' />

      <div
        className={`bg-surface-primary-support flex flex-col rounded-lg ${
          compact ? 'gap-3 p-3' : 'gap-6 p-4'
        }`}
      >
        <button
          type='button'
          className='flex cursor-pointer items-center justify-between rounded
            outline-none focus-visible:ring-1 focus-visible:ring-blue-500
            focus-visible:ring-offset-2'
          onClick={toggleAdvancedSettings}
        >
          <span className='size-p2 text-primary font-bold'>Advanced settings</span>
          <svg
            className={`text-primary h-4 w-4 transition-transform ${
              isAdvancedExpanded ? 'rotate-180' : ''
            }`}
            fill='none'
            stroke='currentColor'
            viewBox='0 0 16 16'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M4 6l4 4 4-4'
            />
          </svg>
        </button>

        {isAdvancedExpanded && triggerSettings.triggerLogicType === 'percentage' && (
          <PercentageAdvancedSettings
            compact={compact}
            idPrefix={idPrefix}
            value={triggerSettings}
            onChange={updateTriggerSettings}
          />
        )}

        {isAdvancedExpanded && triggerSettings.triggerLogicType === 'period' && (
          <PeriodAdvancedSettings
            compact={compact}
            idPrefix={idPrefix}
            value={triggerSettings}
            onChange={updateTriggerSettings}
          />
        )}

        {isAdvancedExpanded && triggerSettings.triggerLogicType === 'spike' && (
          <SpikeAdvancedSettings
            compact={compact}
            idPrefix={idPrefix}
            value={triggerSettings}
            onChange={updateTriggerSettings}
          />
        )}
      </div>
    </>
  );
}

interface TriggerLogicOptionProps {
  title: string;
  description: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  isSelected: boolean;
  onClick: () => void;
  compact: boolean;
}

function TriggerLogicOption({
  title,
  description,
  Icon,
  isSelected,
  onClick,
  compact,
}: TriggerLogicOptionProps) {
  return (
    <button
      type='button'
      className={`flex w-full cursor-pointer rounded-lg transition-colors
        outline-none focus-visible:ring-1 focus-visible:ring-blue-500
        focus-visible:ring-offset-2 ${
          compact ? 'items-start gap-2 px-3 py-3' : 'items-center gap-3 px-2'
        } ${
          isSelected
            ? 'bg-surface-primary-support'
            : compact
              ? ''
              : 'hover:bg-surface-primary-support/50'
        }`}
      onClick={onClick}
    >
      <div className='flex shrink-0 items-center'>
        <div
          className={`flex h-5 w-5 items-center justify-center rounded-full border transition-colors ${
            isSelected
              ? 'bg-focus border-focus'
              : 'bg-surface-primary border-border-primary'
          }`}
        >
          {isSelected && <div className='bg-surface-primary h-2 w-2 rounded-full' />}
        </div>
      </div>

      <div className='flex min-w-0 flex-1 flex-col gap-1 text-left'>
        <div
          className={`size-p2 font-bold ${
            isSelected ? 'text-focus' : 'text-primary'
          }`}
        >
          {title}
        </div>
        {(!compact || isSelected) && (
          <p className='size-caption text-secondary'>{description}</p>
        )}
      </div>

      {!compact && (
        <div className='flex items-center justify-center'>
          <Icon className='text-primary h-34 w-44 shrink-0 mix-blend-darken' />
        </div>
      )}
    </button>
  );
}

function PercentageAdvancedSettings({
  compact,
  idPrefix,
  value,
  onChange,
}: {
  compact: boolean;
  idPrefix: string;
  value: SignalTriggerSettings;
  onChange: (updates: Partial<SignalTriggerSettings>) => void;
}) {
  return (
    <>
      <div className='flex gap-4'>
        <div className='flex flex-1 flex-col gap-1.5'>
          <label className='size-p2 text-primary' htmlFor={`${idPrefix}-percentage-threshold`}>
            Percentage threshold
          </label>
          <NumberInput
            id={`${idPrefix}-percentage-threshold`}
            size={compact ? 'compact' : 'default'}
            value={value.percentageThreshold || ''}
            onChange={(e) => onChange({ percentageThreshold: e.target.value })}
            suffix='%'
          />
        </div>

        <div className='flex flex-1 flex-col gap-1.5'>
          <label className='size-p2 text-primary' htmlFor={`${idPrefix}-evaluation-window`}>
            Evaluation window
          </label>
          <NumberInput
            id={`${idPrefix}-evaluation-window`}
            size={compact ? 'compact' : 'default'}
            value={value.evaluationWindow || ''}
            onChange={(e) => onChange({ evaluationWindow: e.target.value })}
            suffix='min'
          />
        </div>
      </div>

      <div className='flex flex-col gap-1.5'>
        <label className='size-p2 text-primary' htmlFor={`${idPrefix}-cooldown-period`}>
          Cooldown period
        </label>
        <div className='w-49'>
          <NumberInput
            id={`${idPrefix}-cooldown-period`}
            size={compact ? 'compact' : 'default'}
            value={value.cooldownPeriod || ''}
            onChange={(e) => onChange({ cooldownPeriod: e.target.value })}
            suffix='min'
          />
        </div>
        {!compact && (
          <p className='size-caption text-secondary'>
            Prevent repeated alerts for this duration
          </p>
        )}
      </div>
    </>
  );
}

function PeriodAdvancedSettings({
  compact,
  idPrefix,
  value,
  onChange,
}: {
  compact: boolean;
  idPrefix: string;
  value: SignalTriggerSettings;
  onChange: (updates: Partial<SignalTriggerSettings>) => void;
}) {
  return (
    <>
      <div className='flex flex-col gap-1.5'>
        <label className='size-p2 text-primary' htmlFor={`${idPrefix}-period-minutes`}>
          Period
        </label>
        <div className='w-49'>
          <NumberInput
            id={`${idPrefix}-period-minutes`}
            size={compact ? 'compact' : 'default'}
            value={value.periodMinutes || ''}
            onChange={(e) => onChange({ periodMinutes: e.target.value })}
            suffix='min'
          />
        </div>
      </div>

      <div className='flex flex-col gap-1.5'>
        <label className='size-p2 text-primary' htmlFor={`${idPrefix}-cooldown-period-period`}>
          Cooldown period
        </label>
        <div className='w-49'>
          <NumberInput
            id={`${idPrefix}-cooldown-period-period`}
            size={compact ? 'compact' : 'default'}
            value={value.cooldownPeriod || ''}
            onChange={(e) => onChange({ cooldownPeriod: e.target.value })}
            suffix='min'
          />
        </div>
        {!compact && (
          <p className='size-caption text-secondary'>
            Prevent repeated alerts for this duration
          </p>
        )}
      </div>
    </>
  );
}

function SpikeAdvancedSettings({
  compact,
  idPrefix,
  value,
  onChange,
}: {
  compact: boolean;
  idPrefix: string;
  value: SignalTriggerSettings;
  onChange: (updates: Partial<SignalTriggerSettings>) => void;
}) {
  return (
    <>
      <div className='flex gap-4'>
        <div className='flex flex-1 flex-col gap-1.5'>
          <label className='size-p2 text-primary' htmlFor={`${idPrefix}-number-of-spikes`}>
            Number of spikes
          </label>
          <NumberInput
            id={`${idPrefix}-number-of-spikes`}
            size={compact ? 'compact' : 'default'}
            value={value.numberOfSpikes || ''}
            onChange={(e) => onChange({ numberOfSpikes: e.target.value })}
            suffix='spikes'
          />
        </div>

        <div className='flex flex-1 flex-col gap-1.5'>
          <label className='size-p2 text-primary' htmlFor={`${idPrefix}-evaluation-window-spike`}>
            Evaluation window
          </label>
          <NumberInput
            id={`${idPrefix}-evaluation-window-spike`}
            size={compact ? 'compact' : 'default'}
            value={value.evaluationWindow || ''}
            onChange={(e) => onChange({ evaluationWindow: e.target.value })}
            suffix='min'
          />
        </div>
      </div>

      <div className='flex flex-col gap-1.5'>
        <label className='size-p2 text-primary' htmlFor={`${idPrefix}-cooldown-period-spike`}>
          Cooldown period
        </label>
        <div className='w-49'>
          <NumberInput
            id={`${idPrefix}-cooldown-period-spike`}
            size={compact ? 'compact' : 'default'}
            value={value.cooldownPeriod || ''}
            onChange={(e) => onChange({ cooldownPeriod: e.target.value })}
            suffix='min'
          />
        </div>
        {!compact && (
          <p className='size-caption text-secondary'>
            Prevent repeated alerts for this duration
          </p>
        )}
      </div>
    </>
  );
}
